import { createSignal } from "solid-js";

import {
  type AppPalette,
  DEFAULT_PALETTE,
  THEME_VAR_NAMES,
  type ThemeIssue,
  isDefaultPalette,
  themeOverrides,
  validatePalette,
} from "./palette";
import {
  readTenantPalette,
  removeTenantPalette,
  sessionTenantId,
  writeTenantPalette,
} from "./storage";

const [palette, setPalette] = createSignal<AppPalette>(DEFAULT_PALETTE);
const [notice, setNotice] = createSignal<ThemeIssue | null>(null);

let tenantId: string | null = null;
let pendingWrite = false;

export const themePalette = palette;
export const themeNotice = notice;

function clearPaint(): void {
  const root = document.documentElement;
  for (const name of THEME_VAR_NAMES) root.style.removeProperty(name);
}

function paint(next: AppPalette): void {
  clearPaint();
  if (isDefaultPalette(next)) return;
  const root = document.documentElement;
  for (const [name, value] of Object.entries(themeOverrides(next))) {
    root.style.setProperty(name, value);
  }
}

function persist(next: AppPalette): void {
  if (!tenantId) {
    pendingWrite = !isDefaultPalette(next);
    return;
  }
  pendingWrite = false;
  if (isDefaultPalette(next)) removeTenantPalette(tenantId);
  else writeTenantPalette(tenantId, next);
}

function adopt(next: AppPalette): void {
  setPalette(next);
  setNotice(null);
  paint(next);
  persist(next);
}

/** Relie la palette à l'école courante. Une autre école ne réécrit pas celle-ci. */
export function bindThemeTenant(nextTenantId: string | null): void {
  if (nextTenantId === tenantId) return;
  const previous = tenantId;
  tenantId = nextTenantId;
  if (nextTenantId && pendingWrite && previous === null) {
    pendingWrite = false;
    adopt(palette());
    return;
  }
  pendingWrite = false;
  const stored = readTenantPalette(nextTenantId);
  setPalette(stored ?? DEFAULT_PALETTE);
  setNotice(null);
  paint(stored ?? DEFAULT_PALETTE);
}

export function applyStoredTheme(): void {
  bindThemeTenant(sessionTenantId());
}

export function showThemeNotice(issue: ThemeIssue): void {
  setNotice(issue);
}

export function updateTheme(patch: Partial<AppPalette>): ThemeIssue | null {
  const next = { ...palette(), ...patch };
  const issue = validatePalette(next);
  if (issue) {
    setNotice(issue);
    return issue;
  }
  adopt(next);
  return null;
}

export function resetTheme(): void {
  adopt(DEFAULT_PALETTE);
}
